import { CloseOutlined } from "@ant-design/icons";
import {
  DashboardSpreadsheet,
  DataImport,
  DataSourceType,
  LookerFetchMode,
  LookerParams,
} from "@coeff/api";
import { CurvedNoArrow } from "@coeff/components";
import { Button } from "antd";
import React from "react";
import styled from "styled-components";

import { COLORS, SQL_DATASOURCES } from "../constants";
import GmailImportDetails from "../routes/Dashboard/routes/pages/GmailImportDetails";

import {
  Card,
  CardHeader,
  Columns,
  Filter as FilterIcon,
  FiltersAndSorts,
  DEPRECATED_Scheduler,
  SelectedColumns,
  Separator,
  SourceFile,
  SQL as SQLIcon,
  FlexColumn,
  TextWithIcon,
  Typography,
  SettingsBars,
  Paper,
  PaperContent,
  FlexRow,
} from ".";

const SQLQueryWrap = styled.div`
  margin-left: 25px;
  color: ${COLORS.black65};
  font-weight: bold;
  font-family: "Courier New", monospace;
  position: relative;
  overflow: hidden;
  max-height: 300px;
  min-height: 95px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;

  &:after {
    content: "";
    position: absolute;
    bottom: 0px;
    left: 0px;
    right: 0px;
    height: 50px;
    background-image: linear-gradient(transparent, ${COLORS.coeblue});
  }
`;

// CardHeader's flex `.title` defaults to min-width:auto, so a long unbreakable
// SQL table name expands the header and overflows the panel. min-width:0 lets it
// shrink so the inner Typography can ellipsize instead. (ENG-7771)
const TruncatingCardHeader = styled(CardHeader)`
  .title {
    min-width: 0;
  }
`;

type Props = {
  dataImport: DataImport;
  spreadsheet?: DashboardSpreadsheet;
  onClose?: () => void;
};

export const ImportDetailsSidebarContent: React.FC<Props> = ({
  dataImport,
  spreadsheet,
  onClose,
}) => {
  const getSelectedObjectText = (): React.ReactNode => {
    if (
      dataImport?.data_source_type === "salesforce" &&
      dataImport?.source_info?.salesforce_fetch_mode === "objects"
    ) {
      return `Object: ${dataImport?.source_info?.salesforce_object_name}`;
    }
    if (
      dataImport?.data_source_type === "salesforce" &&
      dataImport?.source_info?.salesforce_fetch_mode === "reports"
    ) {
      return `Report: ${dataImport?.source_info?.salesforce_report_name}`;
    }
    if (dataImport?.data_source_type === "hubspot") {
      return `Object: ${dataImport?.source_info?.hubspot_object_name}`;
    }

    if (
      SQL_DATASOURCES.includes(dataImport?.data_source_type ?? "") &&
      !dataImport?.source_info?.sql_is_raw_query_mode
    ) {
      return `Table: ${dataImport?.source_info?.sql_table_name}`;
    }

    if (dataImport?.data_source_type === DataSourceType.Looker) {
      // Looker's config lives in the untyped ppress_params blob rather than as
      // flattened source_info fields, so narrow it to LookerParams here.
      const lookerParams = (dataImport?.source_info?.ppress_params ?? {}) as LookerParams;

      if (lookerParams.fetch_mode === LookerFetchMode.Looks) {
        return lookerParams.looker_look
          ? `Look: ${lookerParams.looker_look.look_name}`
          : lookerParams.looker_dashboard_element
          ? `Dashboard Element: ${lookerParams.looker_dashboard_element.dashboard_element_name}`
          : null;
      }

      if (lookerParams.fetch_mode === LookerFetchMode.ExploreUrl) {
        return lookerParams.looker_explore_url ?? null;
      }

      // Objects mode: the model/explore pair Cartier asked for. `available_models`
      // / `available_explores` are dont_persist, so saved imports only carry the
      // raw LookML identifiers — which are what we want to surface anyway.
      if (lookerParams.selected_model || lookerParams.selected_explore) {
        return `Model: ${lookerParams.selected_model ?? "-"}`;
      }

      return null;
    }

    return null;
  };

  const getLookerExploreText = (): string | null => {
    if (dataImport?.data_source_type !== DataSourceType.Looker) {
      return null;
    }

    const lookerParams = (dataImport?.source_info?.ppress_params ?? {}) as LookerParams;

    if (lookerParams.fetch_mode !== LookerFetchMode.Objects) {
      return null;
    }

    return lookerParams.selected_model || lookerParams.selected_explore
      ? `Explore: ${lookerParams.selected_explore ?? "-"}`
      : null;
  };

  const selectedObject = getSelectedObjectText();

  const lookerExplore = getLookerExploreText();

  const isNetSuiteCrx = dataImport?.data_source_type === "netsuite_crx";

  const isRawQueryMode =
    (dataImport?.data_source_type === "salesforce" &&
      dataImport.source_info?.salesforce_objects_is_raw_query_mode) ||
    (SQL_DATASOURCES.includes(dataImport?.data_source_type ?? "") &&
      dataImport?.source_info?.sql_is_raw_query_mode);

  return (
    <div style={{ overflowY: "auto" }}>
      <div style={{ margin: 15, marginBottom: 20 }}>
        <FlexColumn>
          <FlexRow style={{ justifyContent: "space-between" }}>
            <TextWithIcon
              title={
                <Typography variant="body1" fontWeight={700}>
                  Settings
                </Typography>
              }
              icon={<SettingsBars />}
            />

            {onClose ? (
              <Button type="text" onClick={onClose}>
                <CloseOutlined />
              </Button>
            ) : null}
          </FlexRow>

          <Paper variant="info">
            <PaperContent size="xsmall">
              <Typography color={COLORS.black65}>
                <Typography fontWeight={600}>READ-ONLY</Typography>
                Open in spreadsheet to make changes.
              </Typography>
            </PaperContent>
          </Paper>
        </FlexColumn>
      </div>

      <div style={{ position: "relative", opacity: "0.5" }}>
        <DEPRECATED_Scheduler
          schedule={dataImport?.options?.schedule}
          snapshot_schedule={dataImport?.options?.snapshot_schedule}
          ss_id={spreadsheet?.google_sheets_file_id ?? ""}
        />

        {dataImport?.data_source_type === DataSourceType.Gmail ? (
          <GmailImportDetails dataImport={dataImport} />
        ) : (
          <>
            {!isNetSuiteCrx && (
              <Card className="coeblue coeff__padded">
                <div>
                  {selectedObject && (
                    <>
                      <TruncatingCardHeader
                        title={<Typography isEllipsedContentTooltip>{selectedObject}</Typography>}
                        icon={<SourceFile fill={COLORS.black85} />}
                      />
                      {lookerExplore && (
                        <TruncatingCardHeader
                          title={<Typography isEllipsedContentTooltip>{lookerExplore}</Typography>}
                          icon={
                            <CurvedNoArrow
                              style={{ width: 10, margin: "0px 6px" }}
                              fill={COLORS.black85}
                            />
                          }
                        />
                      )}
                      <Separator />
                    </>
                  )}

                  {!isRawQueryMode && (
                    <>
                      <CardHeader
                        title={`${dataImport?.columns?.length ?? 0} fields`}
                        icon={<Columns />}
                      />
                      <div>
                        <SelectedColumns columns={dataImport?.columns ?? []} />
                      </div>
                    </>
                  )}

                  {isRawQueryMode && (
                    <>
                      <CardHeader
                        title={`Custom S${
                          dataImport?.data_source_type === "salesforce" ? "O" : ""
                        }QL Query`}
                        icon={<SQLIcon />}
                      />
                      <SQLQueryWrap>
                        {dataImport?.source_info?.sql_raw_query ??
                          dataImport?.source_info?.salesforce_soql_query}
                      </SQLQueryWrap>
                    </>
                  )}
                </div>
              </Card>
            )}

            {!isRawQueryMode && (
              <Card className="coeff__padded">
                <div>
                  <CardHeader title="Filter and Sort" icon={<FilterIcon />} />
                  <FiltersAndSorts dataImport={dataImport} />
                </div>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};
